import React, { useEffect, useMemo, useState } from "react";
import {
  FaExclamationTriangle,
  FaLayerGroup,
  FaMedal,
  FaUsers,
} from "react-icons/fa";

import {
  getBeltCountBySeason,
  getBeltsList,
} from "./../../services/beltServices";

import Reveal from "../../components/Reveal/Reveal";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const FALLBACK_BELT = "/img/no-image.jpg";

const BeltCountsSkeleton = () => {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="h-44 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const EmptyState = () => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaMedal />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun décompte trouvé
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        La répartition des ceintures apparaîtra ici dès que les données seront
        disponibles pour la saison en cours.
      </p>
    </div>
  );
};

const ErrorState = ({ message }) => {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
};

const BeltStatCard = ({ icon: Icon, value, label }) => {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
      <Icon className="mx-auto mb-3 text-2xl text-primary" />

      <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>
    </div>
  );
};

const BeltCard = ({ item, index }) => {
  return (
    <Reveal variant="zoom" delay={index * 45}>
      <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-1 shadow-sm transition duration-300 hover:-translate-y-1.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
        <div className="flex min-h-[72px] items-center justify-center rounded-2xl px-1 py-1 dark:bg-white/5">
          <img
            src={item.imageUrl}
            alt={item.name}
            loading="lazy"
            className="max-h-20 w-full object-contain transition duration-300 group-hover:scale-105"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_BELT;
            }}
          />
        </div>

        <div className="mt-1 flex flex-1 flex-col items-center justify-center text-center">
          <h3 className="line-clamp-2 min-h-[30px] text-sm font-bold leading-5 text-slate-700 dark:text-slate-200">
            {item.name}
          </h3>

          <p className="font-barlow text-3xl font-extrabold leading-none text-slate-950 dark:text-white pb-4">
            {item.count}
          </p>
        </div>

        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80 transition group-hover:opacity-100"
        />
      </article>
    </Reveal>
  );
};

const BeltCounts = () => {
  const [beltCounts, setBeltCounts] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const [beltCountData, beltsData] = await Promise.all([
          getBeltCountBySeason(),
          getBeltsList(),
        ]);

        if (!mounted) return;

        setBeltCounts(
          Array.isArray(beltCountData?.belts_distribution)
            ? beltCountData.belts_distribution
            : [],
        );

        setBelts(Array.isArray(beltsData) ? beltsData : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des ceintures :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger la répartition des ceintures.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => {
    if (!beltCounts.length || !belts.length) return [];

    return beltCounts
      .map(({ belt_id, count }) => {
        const beltId = Number(belt_id);
        const belt = belts.find((item) => Number(item.id) === beltId);

        const imageUrl = belt?.belt_url
          ? `/img/belt/${belt.belt_url}`
          : FALLBACK_BELT;

        return {
          id: beltId,
          count: Number(count || 0),
          name: belt?.nom || `Ceinture ${belt_id}`,
          imageUrl,
          order: belts.findIndex((item) => Number(item.id) === beltId),
        };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => {
        if (a.order === -1 && b.order === -1) return a.id - b.id;
        if (a.order === -1) return 1;
        if (b.order === -1) return -1;
        return a.order - b.order;
      });
  }, [beltCounts, belts]);

  const totalLicencies = useMemo(() => {
    return items.reduce((total, item) => total + item.count, 0);
  }, [items]);

  const mostRepresented = useMemo(() => {
    if (!items.length) return null;
    return [...items].sort((a, b) => b.count - a.count)[0];
  }, [items]);

  if (loading) return <BeltCountsSkeleton />;

  return (
    <section
      id="belt"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaMedal />
            Progression des licenciés
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Les ceintures <span className="text-primary">du club</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Répartition des ceintures parmi les licenciés pour la saison en
            cours.
          </p>
        </Reveal>

        {errorMsg && (
          <div className="mt-12">
            <ErrorState message={errorMsg} />
          </div>
        )}

        {!errorMsg && items.length === 0 && (
          <div className="mt-12">
            <EmptyState />
          </div>
        )}

        {!errorMsg && items.length > 0 && (
          <>
            <Reveal
              delay={100}
              className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3"
            >
              <BeltStatCard
                icon={FaUsers}
                value={totalLicencies}
                label="Licenciés"
              />

              <BeltStatCard
                icon={FaLayerGroup}
                value={items.length}
                label="Grades différents"
              />

              <BeltStatCard
                icon={FaMedal}
                value={mostRepresented?.name || "-"}
                label="Majoritaire"
              />
            </Reveal>

            <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
              {items.map((item, index) => (
                <BeltCard key={item.id} item={item} index={index} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default BeltCounts;
